'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Typography, Paper } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import TelegramIcon from '@mui/icons-material/Telegram';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildIcon from '@mui/icons-material/Build';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { HelpSection } from './HelpSection';
import { HelpStep } from './HelpStep';

export function HelpCenter() {
  const t = useTranslations('creator.helpCenter');
  const [expanded, setExpanded] = useState<string | false>(false);

  // Handle hash navigation to expand specific sections
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setExpanded(hash);
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, []);

  const handleChange = (id: string) => {
    setExpanded((prev) => (prev === id ? false : id));
  };

  return (
    <Box>
      {/* Getting Started */}
      <HelpSection
        id="getting-started"
        title={t('gettingStarted.title')}
        description={t('gettingStarted.description')}
        icon={<RocketLaunchIcon color="primary" />}
        expanded={expanded === 'getting-started'}
        onChange={handleChange}
      >
        <HelpStep number={1} text={t('gettingStarted.step1')} />
        <HelpStep number={2} text={t('gettingStarted.step2')} />
        <HelpStep number={3} text={t('gettingStarted.step3')} />
        <HelpStep number={4} text={t('gettingStarted.step4')} />
        <HelpStep number={5} text={t('gettingStarted.step5')} />
        <HelpStep number={6} text={t('gettingStarted.step6')} />
      </HelpSection>

      {/* Content Management */}
      <HelpSection
        id="content-management"
        title={t('contentManagement.title')}
        description={t('contentManagement.description')}
        icon={<DescriptionIcon color="primary" />}
        expanded={expanded === 'content-management'}
        onChange={handleChange}
      >
        <BulletList items={[
          t('contentManagement.textInput'),
          t('contentManagement.fileUpload'),
          t('contentManagement.urlExtraction'),
          t('contentManagement.imageUpload'),
          t('contentManagement.contentLimit'),
          t('contentManagement.contentSources'),
        ]} />
      </HelpSection>

      {/* Bot Settings */}
      <HelpSection
        id="bot-settings"
        title={t('botSettings.title')}
        description={t('botSettings.description')}
        icon={<SettingsIcon color="primary" />}
        expanded={expanded === 'bot-settings'}
        onChange={handleChange}
      >
        <BulletList items={[
          t('botSettings.identity'),
          t('botSettings.slugCheck'),
          t('botSettings.behavior'),
          t('botSettings.publishing'),
        ]} />
      </HelpSection>

      {/* Suggested Questions (Story 7.9) */}
      <HelpSection
        id="suggested-questions"
        title={t('suggestedQuestions.title')}
        description={t('suggestedQuestions.description')}
        icon={<LightbulbIcon color="primary" />}
        expanded={expanded === 'suggested-questions'}
        onChange={handleChange}
      >
        <BulletList items={[
          t('suggestedQuestions.regenerateAll'),
          t('suggestedQuestions.regenerateOne'),
          t('suggestedQuestions.manualEdit'),
          t('suggestedQuestions.disable'),
        ]} />
      </HelpSection>

      {/* Telegram Integration */}
      <HelpSection
        id="telegram-integration"
        title={t('telegramIntegration.title')}
        description={t('telegramIntegration.description')}
        icon={<TelegramIcon color="primary" />}
        expanded={expanded === 'telegram-integration'}
        onChange={handleChange}
      >
        <HelpStep number={1} text={t('telegramIntegration.step1')} />
        <HelpStep number={2} text={t('telegramIntegration.step2')} />
        <HelpStep number={3} text={t('telegramIntegration.step3')} />
        <HelpStep number={4} text={t('telegramIntegration.step4')} />
        <HelpStep number={5} text={t('telegramIntegration.step5')} />
        <HelpStep number={6} text={t('telegramIntegration.step6')} />
        <Paper
          elevation={0}
          sx={{ p: 2, mt: 1, bgcolor: 'grey.50', borderRadius: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('telegramIntegration.settings')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('telegramIntegration.disconnect')}
          </Typography>
        </Paper>
      </HelpSection>

      {/* Analytics */}
      <HelpSection
        id="analytics"
        title={t('analytics.title')}
        description={t('analytics.description')}
        icon={<BarChartIcon color="primary" />}
        expanded={expanded === 'analytics'}
        onChange={handleChange}
      >
        <BulletList items={[
          t('analytics.overview'),
          t('analytics.activityGraph'),
          t('analytics.topQuestions'),
          t('analytics.qualityScore'),
          t('analytics.insights'),
        ]} />
      </HelpSection>

      {/* Managing Your Bot */}
      <HelpSection
        id="managing-bot"
        title={t('managingBot.title')}
        description={t('managingBot.description')}
        icon={<BuildIcon color="primary" />}
        expanded={expanded === 'managing-bot'}
        onChange={handleChange}
      >
        <BulletList items={[
          t('managingBot.editContent'),
          t('managingBot.deactivate'),
          t('managingBot.reactivate'),
          t('managingBot.delete'),
        ]} />
      </HelpSection>

      {/* Account Settings */}
      <HelpSection
        id="account-settings"
        title={t('accountSettings.title')}
        description={t('accountSettings.description')}
        icon={<PersonIcon color="primary" />}
        expanded={expanded === 'account-settings'}
        onChange={handleChange}
      >
        <BulletList items={[
          t('accountSettings.emailPreferences'),
        ]} />
      </HelpSection>

      {/* Sharing Your Bot */}
      <HelpSection
        id="sharing-bot"
        title={t('sharingBot.title')}
        description={t('sharingBot.description')}
        icon={<ShareIcon color="primary" />}
        expanded={expanded === 'sharing-bot'}
        onChange={handleChange}
      >
        <BulletList items={[
          t('sharingBot.publicLink'),
          t('sharingBot.socialSharing'),
        ]} />
      </HelpSection>
    </Box>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {items.map((item, index) => (
        <Typography
          key={index}
          component="li"
          variant="body2"
          sx={{ mb: 0.75, lineHeight: 1.6 }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
}
